import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {forwardRef, useImperativeHandle, useRef, useState} from "react";

import MyCheckbox from "../../components/MyCheckbox";
import MyMultiSelectCheckboxes from "../../components/MyMultiSelectCheckboxes";
import MySelect from "../../components/MySelect";
import MyTextField from "../../components/MyTextField";
import ShiptoSearch from "../../components/ShiptoSearch";
import {DATA_TYPES, RETAIL_LOCATION_FIELDS, VIEW_TYPES} from "../../constants";
import {useBusinessUnitCodes} from "../../hooks/useBusinessUnitCodes";
import {useCountries} from "../../hooks/useCountries";
import {useRetailLocation} from "../../hooks/useRetailLocation";
import {useRetailLocationCreate} from "../../hooks/useRetailLocationCreate";
import {useRetailLocationUpdate} from "../../hooks/useRetailLocationUpdate";
import {useSelectFitCategories} from "../../hooks/useSelectFitCategories";
import {useSelectFitEbsCodes} from "../../hooks/useSelectFitEbsCodes";
import {useSelectFitLogoTypes} from "../../hooks/useSelectFitLogoTypes";
import {useStates} from "../../hooks/useStates";
import {useAddressValidate} from "../../hooks/useAddressValidate";
import {useCurrentUser} from "../../hooks/useCurrentUser";

const getInitialFormData = () => {
    const initialFormData = {};

    RETAIL_LOCATION_FIELDS.forEach((field) => {
        initialFormData[field.name] = {};
        switch (field.viewType) {
            case VIEW_TYPES.TEXT:
                initialFormData[field.name].value = "";
                break;
            case VIEW_TYPES.SINGLE_SELECT:
                initialFormData[field.name].value = "";
                initialFormData[field.name].options = field.name === "countryCode" ? [{id: "US", name: "US"}] : [];
                break;
            case VIEW_TYPES.MULTI_SELECT:
                initialFormData[field.name].value = [];
                initialFormData[field.name].options = [];
                break;
            case VIEW_TYPES.CHECKBOX:
                initialFormData[field.name].value = field.defaultValue;
                break;
        }
    });

    return initialFormData;
};

const RetailLocationModal = forwardRef(({refetchRetailLocations}, ref) => {
    const [resetKey, setResetKey] = useState(0);
    const [retailLocationId, setRetailLocationId] = useState(0);
    const [open, setOpen] = useState(false);
    const [errorMessages, setErrorMessages] = useState({});
    const [formData, setFormData] = useState(getInitialFormData);
    const [mode, setMode] = useState("create");
    const [confirmationDialog, setConfirmationDialog] = useState({open: false});
    const [confirmationAddressDialog, setConfirmationAddressDialog] = useState({open: false});

    const overlayRef = useRef(null);

    useImperativeHandle(
        ref,
        () => ({
            handleOpen,
        }),
        []
    );
    const resetSelectFits = () => {
        setFormData((prev) => {
            return {
                ...prev,
                selectFitEbsCode: {value: "", options: []},
                selectFitCategory: {value: "", options: []},
                selectFitLogoType: {value: "", options: []},
            };
        });
    };
    const retailLocation = useRetailLocation(retailLocationId, open, (data) => {
        setFormData((prev) => {
            let newFormData = {...prev};
            RETAIL_LOCATION_FIELDS.forEach((field) => {
                switch (field.dataType) {
                    case DATA_TYPES.STRING:
                    case DATA_TYPES.TIMESTAMP:
                        newFormData[field.name] = {
                            ...prev[field.name],
                            value: data[field.name] ?? "",
                        };
                        break;
                    case DATA_TYPES.BOOLEAN:
                        newFormData[field.name] = {
                            ...prev[field.name],
                            value: !!data[field.name],
                        };
                        break;
                    case DATA_TYPES.STRING_ARRAY:
                        newFormData[field.name] = {
                            ...prev[field.name],
                            value: data[field.name] ?? [],
                        };
                        break;
                    case DATA_TYPES.OBJECT:
                        newFormData[field.name] = {
                            ...prev[field.name],
                            value: data[field.name]?.id ?? "",
                        };
                        break;
                }
            });

            return newFormData;
        });
    });

    useSelectFitEbsCodes(
        open,
        resetKey,
        (data) => {
            setFormData((prev) => {
                // Add a blank option as the first option
                const optionsWithBlank = [{id: "", name: ""}, ...(data || [])];
                return {...prev, selectFitEbsCode: {...prev.selectFitEbsCode, options: optionsWithBlank}};
            });
        },
        {businessUnitCode: formData.businessUnitCode.value}
    );

    useSelectFitCategories(
        open,
        resetKey,
        (data) => {
            setFormData((prev) => {
                // Add a blank option as the first option
                const optionsWithBlank = [{id: "", name: ""}, ...(data || [])];
                return {...prev, selectFitCategory: {...prev.selectFitCategory, options: optionsWithBlank}};
            });
        },
        {businessUnitCode: formData.businessUnitCode.value}
    );

    useSelectFitLogoTypes(
        open,
        resetKey,
        (data) => {
            setFormData((prev) => {
                // Add a blank option as the first option
                const optionsWithBlank = [{id: "", name: ""}, ...(data || [])];
                return {...prev, selectFitLogoType: {...prev.selectFitLogoType, options: optionsWithBlank}};
            });
        },
        {businessUnitCode: formData.businessUnitCode.value}
    );

    useBusinessUnitCodes(open && mode === "create", resetKey, (data) => {
        setFormData((prev) => {
            return {
                ...prev,
                businessUnitCode: {...prev.businessUnitCode, options: data, value: currentUser?.businessUnitCode},
            };
        });
    });

    useCountries(formData.businessUnitCode.value, open, (data) => {
        setFormData((prev) => {
            if (mode === "update") {
                return {...prev, countryCode: {...prev.countryCode, options: data}};
            } else {
                const defaultCountry = data ? data[0]?.id : "";
                return {
                    ...prev,
                    countryCode: {...prev.countryCode, options: data, value: defaultCountry},
                };
            }
        });
    });
    useStates(formData.businessUnitCode.value, formData.countryCode.value, open, (data) => {
        setFormData((prev) => {
            if (mode === "update") {
                return {...prev, state: {...prev.state, options: data}};
            } else {
                const defaultState = data ? data[0]?.id : "";
                return {
                    ...prev,
                    state: {...prev.state, options: data, value: defaultState},
                };
            }
        });
    });
    const [currentUser, updateCurrentUser] = useCurrentUser();

    const retailLocationUpdateMutation = useRetailLocationUpdate();
    const retailLocationCreateMutation = useRetailLocationCreate();
    const addressValidateMutation = useAddressValidate();

    const handleInputChange = (key, value) => {
        setFormData((prev) => {
            let newFormData = {...prev, [key]: {...prev[key], value: value}};
            return newFormData;
        });
        if (key === "businessUnitCode") {
            resetSelectFits();
        }
    };

    const handleShiptoChange = (value) => {
        if (value) {
            setFormData((prev) => {
                let newFormData = {
                    ...prev,
                    shiptoId: {...prev["shiptoId"], value: value.shiptoId},
                    displayName: {...prev["displayName"], value: value.name ?? prev["displayName"]?.value ?? ""},
                    street: {
                        ...prev["street"],
                        value: [value.address1, value.address2, value.address3].join(", "),
                    },
                    city: {...prev["city"], value: value.city},
                    state: {...prev["state"], value: value.state},
                    postalCode: {...prev["postalCode"], value: value.postalCode},
                    countryCode: {...prev["countryCode"], value: value.country},
                };
                return newFormData;
            });
        }
    };

    const handleOpen = (retailLocationId) => {
        if (!!retailLocationId) {
            setRetailLocationId(retailLocationId);
            setMode("update");
        } else {
            setRetailLocationId(0);
            setMode("create");
        }
        setOpen(true);
    };

    const handleClose = () => {
        setErrorMessages({});
        setOpen(false);
        setRetailLocationId(0);
        setFormData((prev) => {
            return getInitialFormData();
        });
        setResetKey((prev) => prev + 1);
    };

    const composePayload = () => {
        let payload = {};
        RETAIL_LOCATION_FIELDS.forEach((field) => {
            if (field.key) {
                switch (field.dataType) {
                    case DATA_TYPES.BOOLEAN:
                        payload[field.key] = !!formData[field.name].value;
                        break;
                    case DATA_TYPES.STRING_ARRAY:
                        let filteredValue = formData[field.name].value.filter((id) =>
                            formData[field.name].options.some((option) => option.id === id)
                        );
                        if (filteredValue.length > 0) {
                            payload[field.key] = filteredValue;
                        }
                        break;
                    default:
                        // For Select Fit dropdowns, if blank option is chosen, submit null
                        if (
                            field.name === "selectFitEbsCode" ||
                            field.name === "selectFitCategory" ||
                            field.name === "selectFitLogoType"
                        ) {
                            const v = formData[field.name].value;
                            payload[field.key] = v === "" || v === null || typeof v === "undefined" ? null : v;
                        } else {
                            if (formData[field.name].value !== "" && formData[field.name].value != null) {
                                payload[field.key] = formData[field.name].value;
                            }
                        }
                        break;
                }
            }
        });
        if (retailLocationId) {
            payload.id = retailLocationId;
        }

        return payload;
    };

    const onCreateSuccess = (response) => {
        refetchRetailLocations();
        handleClose();
    };

    const onUpdateSuccess = () => {
        refetchRetailLocations();
        handleClose();
    };
    const onValidateAddressSuccess = (response) => {
        if (response?.valid) {
            handleSubmit();
        } else {
            setConfirmationAddressDialog({
                open: true,
                reason: response?.reason,
                suggestedAddress: response?.suggestedAddress,
            });
        }
    };
    const onValidateAddressError = (error) => {
        setErrorMessages({
            retailLocationView: error?.response?.data?.message || "Unable to validate the address. Please try again.",
        });
    };
    const onValidateAddress = () => {
        setErrorMessages({});
        const payload = composePayload();
        const street = payload.street || "";
        const commaIndex = street.indexOf(",");
        let address1;
        let address2;
        if (commaIndex === -1) {
            address1 = street.trim();
            address2 = undefined;
        } else {
            address1 = street.slice(0, commaIndex).trim();
            const rest = street.slice(commaIndex + 1).trim();
            address2 = rest ? rest : undefined;
        }
        addressValidateMutation.mutate(
            {
                address1,
                address2,
                city: payload.city,
                state: payload.state,
                postalCode: payload.postalCode,
                country: payload.countryCode,
            },
            {
                onSuccess: onValidateAddressSuccess,
                onError: onValidateAddressError,
            }
        );
    };
    const onContinueAndSaveAddress = () => {
        setConfirmationAddressDialog({open: false});
        handleSubmit();
    };
    const onUseSuggestedAddress = () => {
        const {address1, address2, city, state, postalCode, country} = confirmationAddressDialog.suggestedAddress;
        setFormData((prev) => ({
            ...prev,
            street: {
                ...prev.street,
                value: address2 ? (address1 ?? "") + ", " + address2 : address1 ?? "",
            },
            city: {...prev.city, value: city ?? ""},
            state: {...prev.state, value: state ?? ""},
            postalCode: {...prev.postalCode, value: postalCode ?? ""},
            countryCode: {...prev.countryCode, value: country ?? ""},
        }));
        setConfirmationAddressDialog({open: false});
    };
    const onSubmitError = (error) => {
        setErrorMessages({retailLocationView: error?.response?.data?.message});
    };

    const handleSubmit = () => {
        // handleClose();
        const payload = composePayload();
        const action = mode === "create" ? "Creating" : "Updating";
        if (mode === "create") {
            retailLocationCreateMutation.mutate(payload, {
                onSuccess: onCreateSuccess,
                onError: onSubmitError,
            });
        } else {
            retailLocationUpdateMutation.mutate(payload, {
                onSuccess: onUpdateSuccess,
                onError: onSubmitError,
            });
        }
        // handleClose();
    };

    const calculateOverlayTextAngle = (element) => {
        if (element) {
            const {width, height} = element.getBoundingClientRect();
            const radianAngle = Math.atan2(height, width);
            return -radianAngle * (180 / Math.PI);
        }
        return 0;
    };

    return (
        <>
            <Dialog
                open={open}
                fullWidth={true}
                maxWidth="md"
                PaperProps={{
                    component: "form",
                    onSubmit: (e) => {
                        e.preventDefault();
                        onValidateAddress();
                    },
                }}
            >
                <DialogTitle zIndex={3}>
                    {retailLocationId && retailLocation ? retailLocation.displayName : "New Retail Location"}
                    {mode === "update" && (
                        <Button
                            sx={{
                                float: "right",
                                textDecoration: "underline",
                            }}
                            onClick={() => {
                                setFormData((prev) => {
                                    return {
                                        ...prev,
                                        active: {...prev.active, value: !prev.active.value},
                                    };
                                });
                            }}
                        >
                            {formData.active.value ? "Deactivate" : "Re-activate"}
                        </Button>
                    )}
                </DialogTitle>
                <DialogContent ref={overlayRef} dividers={true}>
                    {!formData.active.value && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                backgroundColor: "rgba(255, 255, 255, 0.5)",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                backdropFilter: "blur(3px)",
                                zIndex: 2,
                            }}
                        >
                            <Typography
                                variant="h1"
                                letterSpacing={10}
                                fontWeight={700}
                                textTransform={"uppercase"}
                                sx={{
                                    transform: `rotate(${calculateOverlayTextAngle(overlayRef.current)}deg)`,
                                }}
                            >
                                Inactive
                            </Typography>
                        </Box>
                    )}
                    <Grid container spacing={1}>
                        {RETAIL_LOCATION_FIELDS.filter((field) => {
                            return field.modes.includes(mode);
                        }).map((field) => {
                            if (field.name === "shiptoId") {
                                // In update mode, show Ship-to as a disabled text field
                                if (mode === "update") {
                                    return (
                                        <Grid item xs={12} key={field.name}>
                                            <MyTextField
                                                value={formData.shiptoId.value}
                                                label={field.label}
                                                type="text"
                                                editable={false}
                                                required={false}
                                            />
                                        </Grid>
                                    );
                                }

                                // In create mode, show the ShiptoSearch dropdown
                                return (
                                    <Grid item xs={12} key={field.name}>
                                        <ShiptoSearch
                                            label={field.label}
                                            businessUnitCode={formData.businessUnitCode.value}
                                            editable={!!formData["businessUnitCode"].value}
                                            initialValue={formData.shiptoId.value}
                                            onChange={(value) => handleShiptoChange(value)}
                                        />
                                    </Grid>
                                );
                            }
                            switch (field.viewType) {
                                case VIEW_TYPES.TEXT:
                                    let textValue = formData[field.name].value;
                                    if (field.dataType === DATA_TYPES.TIMESTAMP) {
                                        textValue = textValue ? new Date(textValue).toLocaleString() : "";
                                    }
                                    return (
                                        <Grid item xs={12} key={field.name}>
                                            <MyTextField
                                                value={textValue}
                                                label={field.label}
                                                type="text"
                                                editable={!!formData["businessUnitCode"].value}
                                                required={mode === "create" && field.requiredForCreate}
                                                maxLength={field.maxLength}
                                                onChange={(event) => handleInputChange(field.name, event.target.value)}
                                            />
                                        </Grid>
                                    );
                                case VIEW_TYPES.SINGLE_SELECT:
                                    let singleSelectValue = formData[field.name].value;
                                    let options = formData[field.name].options;
                                    if (options.length === 0) {
                                        if (field.dataType === DATA_TYPES.OBJECT) {
                                            options = [
                                                {
                                                    id: retailLocation[field.name]?.id ?? "",
                                                    name: retailLocation[field.name]?.name ?? "",
                                                },
                                            ];
                                        } else {
                                            options = [
                                                {
                                                    id: retailLocation[field.name] ?? "",
                                                    name: retailLocation[field.name] ?? "",
                                                },
                                            ];
                                        }
                                    }
                                    return (
                                        <Grid item xs={12} key={field.name}>
                                            <MySelect
                                                key={resetKey}
                                                label={field.label}
                                                options={options}
                                                value={singleSelectValue}
                                                editable={
                                                    field.name === "businessUnitCode" ||
                                                    !!formData["businessUnitCode"].value
                                                }
                                                required={mode === "create" && field.requiredForCreate}
                                                autoSelect={singleSelectValue || mode === "create"}
                                                onChange={(value) => handleInputChange(field.name, value)}
                                            />
                                        </Grid>
                                    );
                                case VIEW_TYPES.MULTI_SELECT:
                                    return (
                                        <Grid item xs={12} key={field.name}>
                                            <MyMultiSelectCheckboxes
                                                title={field.label}
                                                options={formData[field.name].options}
                                                value={formData[field.name].value}
                                                onChange={(value) => handleInputChange(field.name, value)}
                                            />
                                        </Grid>
                                    );
                                case VIEW_TYPES.CHECKBOX:
                                    let checkBoxValue = formData[field.name].value;
                                    return (
                                        <Grid item xs={12} key={field.name}>
                                            <MyCheckbox
                                                checked={!!checkBoxValue}
                                                label={field.label}
                                                editable={!!formData["businessUnitCode"].value}
                                                onChange={(event) =>
                                                    handleInputChange(field.name, event.target.checked)
                                                }
                                            />
                                        </Grid>
                                    );
                            }
                        })}
                    </Grid>
                </DialogContent>
                <DialogActions
                    sx={{
                        m: 2,
                        zIndex: 3,
                    }}
                >
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        autoFocus
                        // disabled={!formEditable}
                    >
                        {mode === "update" ? "UPDATE" : "CREATE"}
                    </Button>
                </DialogActions>
                {errorMessages.retailLocationView && (
                    <Box mt={2}>
                        <Alert severity="error">{errorMessages.retailLocationView}</Alert>
                    </Box>
                )}
            </Dialog>
            <Dialog open={confirmationDialog.open}>
                <DialogTitle>{confirmationDialog.emailSent ? "Success" : "Warning"}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmationDialog.emailSent
                            ? "A welcome email has been sent to " + confirmationDialog.email
                            : "Failed to send welcome email to " + confirmationDialog.email}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmationDialog((prev) => ({...prev, open: false}))}
                        color="primary"
                        autoFocus
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={confirmationAddressDialog.open}>
                <DialogTitle>Warning</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmationAddressDialog.reason} Click <b>Cancel</b> to go back and update the address.{" "}
                        <b>Continue</b> and save with entered address?
                    </DialogContentText>
                    {confirmationAddressDialog.suggestedAddress && (
                        <Box mt={2}>
                            <Typography variant="subtitle2">Suggested address</Typography>
                            <Typography variant="body2">
                                {confirmationAddressDialog.suggestedAddress.address1}
                            </Typography>
                            {confirmationAddressDialog.suggestedAddress.address2 && (
                                <Typography variant="body2">
                                    {confirmationAddressDialog.suggestedAddress.address2}
                                </Typography>
                            )}
                            <Typography variant="body2">
                                {confirmationAddressDialog.suggestedAddress.city},{" "}
                                {confirmationAddressDialog.suggestedAddress.state}{" "}
                                {confirmationAddressDialog.suggestedAddress.postalCode}
                            </Typography>
                            <Typography variant="body2">
                                {confirmationAddressDialog.suggestedAddress.country}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {confirmationAddressDialog.suggestedAddress && (
                        <Button onClick={() => onUseSuggestedAddress()} color="primary">
                            Use suggested address
                        </Button>
                    )}
                    <Button
                        onClick={() => setConfirmationAddressDialog((prev) => ({...prev, open: false}))}
                        variant="contained"
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button onClick={() => onContinueAndSaveAddress()} color="primary">
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
});

export default RetailLocationModal;
